/**
 * Cart Interceptor for Shopify Theme
 * 
 * This script intercepts cart actions and sends them to your webhook endpoint
 * to implement cart hold functionality without requiring cart webhook permissions.
 * 
 * Usage: Add this script to your theme and configure the webhook URL
 */

// Save original fetch before it gets intercepted
const originalCartFetch = window.fetch.bind(window);

// Configuration - Use global config if available, otherwise use defaults
const CART_WEBHOOK_CONFIG = window.CART_WEBHOOK_CONFIG || {
  webhookUrl: 'https://shopify.infinitediscs.com/webhook/theme_webhook.php', // Your webhook endpoint
  apiKey: 'c0ade8be7fe6165b9fa0d8eb0779efb7bb9eafe9e65914794d70c52bc8c34a6f', // Your API key from the dashboard (get from shops table)
  shop: 'disc-source.myshopify.com', // Your shop domain (e.g., mystore.myshopify.com)
  enableLogging: true // Set to false in production
};

console.log('[Cart Interceptor] Loading cart interceptor script...');
console.log('[Cart Interceptor] Configuration loaded:', CART_WEBHOOK_CONFIG);

// Utility function to log cart events
function logCartEvent(event, data) {
  if (CART_WEBHOOK_CONFIG.enableLogging) {
    console.log(`[Cart Interceptor] ${event}:`, data);
  }
}

// Function to send cart data to webhook
async function sendCartEvent(eventType, cartData, additionalData = {}) {
  try {
    const payload = {
      event_type: eventType,
      shop: CART_WEBHOOK_CONFIG.shop,
      timestamp: new Date().toISOString(),
      cart_data: cartData,
      ...additionalData
    };

    logCartEvent(eventType, payload);

    // Use original fetch to avoid interceptor recursion
    const response = await originalCartFetch(CART_WEBHOOK_CONFIG.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CART_WEBHOOK_CONFIG.apiKey
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    logCartEvent(`${eventType}_response`, result);
    
    return result;
  } catch (error) {
    console.error(`[Cart Interceptor] Error sending ${eventType}:`, error);
    return { success: false, error: error.message };
  }
}

// Enhanced CartItems class with webhook integration
class CartItemsWithWebhook extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        // Send cart update to webhook
        this.sendCartUpdateToWebhook(parsedState, eventTarget, variantId);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  // New method to send cart updates to webhook
  async sendCartUpdateToWebhook(cartData, eventType, variantId) {
    const cartEventData = {
      cart_token: cartData.token,
      item_count: cartData.item_count,
      total_price: cartData.total_price,
      items: (cartData.items || []).map(item => ({
        id: item.id,
        variant_id: item.variant_id,
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        title: item.title,
        variant_title: item.variant_title,
        sku: item.sku
      })),
      customer: cartData.customer || null,
      attributes: cartData.attributes || {}
    };

    // Check if this is a removal that results in empty cart
    if (cartData.item_count === 0 && eventType === 'update') {
      // This is actually a remove operation that cleared the cart
      // Try to get the previous cart state to include item details
      try {
        const previousCartData = localStorage.getItem('previous_cart_state');
        if (previousCartData) {
          const prevCart = JSON.parse(previousCartData);
          const removedItem = prevCart.items.find(item => item.variant_id == variantId);
          
          await sendCartEvent('carts/remove', cartEventData, {
            variant_id: variantId,
            action: 'remove_item',
            is_last_item: true,
            cart_cleared: true,
            removed_item: removedItem ? {
              id: removedItem.id,
              variant_id: removedItem.variant_id,
              product_id: removedItem.product_id,
              quantity: removedItem.quantity,
              price: removedItem.price,
              title: removedItem.title,
              variant_title: removedItem.variant_title,
              sku: removedItem.sku
            } : null
          });
        } else {
          await sendCartEvent('carts/remove', cartEventData, {
            variant_id: variantId,
            action: 'remove_item',
            is_last_item: true,
            cart_cleared: true
          });
        }
      } catch (error) {
        console.error('[Cart Update] Error handling remove event:', error);
        await sendCartEvent('carts/remove', cartEventData, {
          variant_id: variantId,
          action: 'remove_item',
          is_last_item: true,
          cart_cleared: true
        });
      }
    } else {
      await sendCartEvent(`carts/${eventType}`, cartEventData, {
        variant_id: variantId,
        line_item_changed: true
      });
    }
    
    // Store current cart state for next comparison
    localStorage.setItem('previous_cart_state', JSON.stringify(cartData));
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

// Enhanced product form handler for add to cart
class ProductFormWithWebhook extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form.querySelector('[name=add]').addEventListener('click', this.onSubmitHandler.bind(this));
  }

  async onSubmitHandler(event) {
    event.preventDefault();
    
    const submitButton = this.form.querySelector('[name=add]');
    const originalText = submitButton.textContent;
    
    // Show loading state
    submitButton.textContent = 'Adding...';
    submitButton.disabled = true;

    try {
      // Get form data
      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity')) || 1;

      // Add to cart via Shopify API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cartData = await response.json();
      
      // Send cart creation/update to webhook
      await this.sendAddToCartToWebhook(cartData, variantId, quantity);

      // Trigger cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: cartData });

      // Show success message or update cart drawer
      this.showSuccessMessage();

    } catch (error) {
      console.error('[Product Form] Error adding to cart:', error);
      this.showErrorMessage(error.message);
    } finally {
      // Restore button state
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }

  async sendAddToCartToWebhook(cartData, variantId, quantity) {
    try {
      console.log('[Product Form] sendAddToCartToWebhook called with:', { cartData, variantId, quantity });
      
      const cartEventData = {
        cart_token: cartData.token,
        item_count: cartData.item_count,
        total_price: cartData.total_price,
        items: (cartData.items || []).map(item => ({
          id: item.id,
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          title: item.title,
          variant_title: item.variant_title,
          sku: item.sku
        })),
        customer: cartData.customer || null,
        attributes: cartData.attributes || {}
      };

      await sendCartEvent('carts/create', cartEventData, {
        variant_id: variantId,
        quantity_added: quantity,
        action: 'add_to_cart'
      });
    } catch (error) {
      console.error('[Product Form] Error in sendAddToCartToWebhook:', error);
      // Don't throw the error, just log it to prevent breaking the cart functionality
    }
  }

  showSuccessMessage() {
    // You can customize this to show a success message
    console.log('[Product Form] Item added to cart successfully');
  }

  showErrorMessage(message) {
    // You can customize this to show an error message
    console.error('[Product Form] Error:', message);
  }
}

// Register the enhanced components (only if not already defined)
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItemsWithWebhook);
}
if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductFormWithWebhook);
}

// Enhanced cart-remove-button with webhook support (only if not already defined)
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', async (event) => {
        event.preventDefault();
        const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
        const variantId = this.dataset.variantId || this.dataset.id;
        
        // Get cart state BEFORE removal
        const response = await fetch('/cart.js');
        const cartDataBeforeRemoval = await response.json();
        
        // Find the item being removed
        const itemToRemove = cartDataBeforeRemoval.items.find(item => 
          item.variant_id == variantId || item.id == variantId
        );
        
        // Send remove event to webhook with the item details
        await this.sendRemoveToWebhook(variantId, itemToRemove, cartDataBeforeRemoval);
        
        cartItems.updateQuantity(this.dataset.index, 0, event);
      });
    }

    async sendRemoveToWebhook(variantId, itemToRemove, cartDataBeforeRemoval) {
      try {
        // Use the cart state before removal to get accurate item details
        const cartEventData = {
          cart_token: cartDataBeforeRemoval.token,
          item_count: cartDataBeforeRemoval.item_count,
          total_price: cartDataBeforeRemoval.total_price,
          items: cartDataBeforeRemoval.items.map(item => ({
            id: item.id,
            variant_id: item.variant_id,
            product_id: item.product_id,
            quantity: item.quantity,
            price: item.price,
            title: item.title,
            variant_title: item.variant_title,
            sku: item.sku
          })),
          customer: cartDataBeforeRemoval.customer || null,
          attributes: cartDataBeforeRemoval.attributes || {}
        };

        // Include the specific item being removed
        const removeEventData = {
          variant_id: variantId,
          action: 'remove_item',
          removed_item: itemToRemove ? {
            id: itemToRemove.id,
            variant_id: itemToRemove.variant_id,
            product_id: itemToRemove.product_id,
            quantity: itemToRemove.quantity,
            price: itemToRemove.price,
            title: itemToRemove.title,
            variant_title: itemToRemove.variant_title,
            sku: itemToRemove.sku
          } : null,
          is_last_item: cartDataBeforeRemoval.item_count === 1
        };

        await sendCartEvent('carts/remove', cartEventData, removeEventData);
      } catch (error) {
        console.error('[Cart Remove] Error sending remove to webhook:', error);
      }
    }
  }
  
  customElements.define('cart-remove-button', CartRemoveButton);
}

// Enhanced cart note with webhook
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => {
                CartPerformance.measureFromEvent('note-update:user-action', event);
                // Send note update to webhook
                sendCartEvent('carts/update', { note: event.target.value }, { action: 'note_update' });
              });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Enhanced cart clear button handler (only if not already defined)
if (!customElements.get('cart-clear-button')) {
  class CartClearButton extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', async (event) => {
        event.preventDefault();
        
        try {
          // Get current cart state before clearing
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          
          // Send clear event to webhook
          await this.sendClearToWebhook(cartData);
          
          // Clear the cart via Shopify API
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          // Trigger cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-clear', cartData: { item_count: 0, items: [] } });
          
          // Reload the page or update cart display
          window.location.reload();
          
        } catch (error) {
          console.error('[Cart Clear] Error clearing cart:', error);
        }
      });
    }

    async sendClearToWebhook(cartData) {
      const cartEventData = {
        cart_token: cartData.token,
        item_count: cartData.item_count,
        total_price: cartData.total_price,
        items: (cartData.items || []).map(item => ({
          id: item.id,
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          title: item.title,
          variant_title: item.variant_title,
          sku: item.sku
        })),
        customer: cartData.customer || null,
        attributes: cartData.attributes || {}
      };

      await sendCartEvent('carts/clear', cartEventData, {
        action: 'clear_cart'
      });
    }
  }
  
  // Register the cart clear button
  customElements.define('cart-clear-button', CartClearButton);
}

// Initialize cart interceptor
document.addEventListener('DOMContentLoaded', function() {
  logCartEvent('init', 'Cart interceptor initialized');
  
  // Send initial cart state if cart is not empty
  if (window.cart && window.cart.item_count > 0) {
    sendCartEvent('carts/update', window.cart, { action: 'page_load' });
  }
  
  // Add event listeners for cart clearing
  document.addEventListener('click', function(event) {
    // Handle cart clear buttons (various selectors)
    if (event.target.matches('[data-cart-clear], .cart-clear, .clear-cart, [data-action="clear-cart"]')) {
      event.preventDefault();
      const clearButton = new CartClearButton();
      clearButton.click();
    }
  });
  
  // Global cart update interceptor to catch all cart changes
  let lastCartState = null;
  
  // Function to check for cart changes and send appropriate webhooks
  async function checkCartChanges() {
    try {
      const response = await originalCartFetch('/cart.js');
      const currentCart = await response.json();
      
      if (lastCartState && lastCartState.item_count !== currentCart.item_count) {
        console.log('[Cart Interceptor] Cart item count changed:', {
          previous: lastCartState.item_count,
          current: currentCart.item_count
        });
        
        // If cart went from having items to empty, it's a remove operation
        if (lastCartState.item_count > 0 && currentCart.item_count === 0) {
          console.log('[Cart Interceptor] Cart was cleared, sending remove webhook');
          
          const cartEventData = {
            cart_token: lastCartState.token,
            item_count: lastCartState.item_count,
            total_price: lastCartState.total_price,
            items: lastCartState.items.map(item => ({
              id: item.id,
              variant_id: item.variant_id,
              product_id: item.product_id,
              quantity: item.quantity,
              price: item.price,
              title: item.title,
              variant_title: item.variant_title,
              sku: item.sku
            })),
            customer: lastCartState.customer || null,
            attributes: lastCartState.attributes || {}
          };
          
          // Send remove webhook with the last item details
          const lastItem = lastCartState.items[lastCartState.items.length - 1];
          await sendCartEvent('carts/remove', cartEventData, {
            variant_id: lastItem ? lastItem.variant_id : null,
            action: 'remove_item',
            is_last_item: true,
            cart_cleared: true,
            removed_item: lastItem ? {
              id: lastItem.id,
              variant_id: lastItem.variant_id,
              product_id: lastItem.product_id,
              quantity: lastItem.quantity,
              price: lastItem.price,
              title: lastItem.title,
              variant_title: lastItem.variant_title,
              sku: lastItem.sku
            } : null
          });
        }
      }
      
      lastCartState = currentCart;
    } catch (error) {
      console.error('[Cart Interceptor] Error checking cart changes:', error);
    }
  }
  
  // Check for cart changes every 500ms
  setInterval(checkCartChanges, 500);
  
  // Also check on cart update events
  document.addEventListener('cart:updated', checkCartChanges);
  document.addEventListener('cart:refresh', checkCartChanges);
});

 